import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Camera, Upload, Trash2, X, MessageSquare, Filter, Eye, EyeOff, ZoomIn } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Iter164 — extended categories. 'Defect' for inspection-stage damage
// shots that can be linked to Remedial Repair leak-areas; 'Example'
// for reference shots (Phase C library, deferred).
const CATEGORIES = ['Before', 'During', 'After', 'Defect', 'Example', 'Other'];
const CATEGORY_COLORS = {
  Before:  'bg-amber-100 text-amber-800 border-amber-200',
  During:  'bg-blue-100 text-blue-800 border-blue-200',
  After:   'bg-emerald-100 text-emerald-800 border-emerald-200',
  Defect:  'bg-rose-100 text-rose-800 border-rose-200',
  Example: 'bg-violet-100 text-violet-800 border-violet-200',
  Other:   'bg-slate-100 text-slate-700 border-slate-200',
};

export default function PhotoGallery({ appraisalId }) {
  const { api } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [description, setDescription] = useState('');
  // Photo category to apply to the next upload batch + active gallery filter.
  const [uploadCategory, setUploadCategory] = useState('Before');
  const [filterCategory, setFilterCategory] = useState('All');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/photos/${appraisalId}`);
      setPhotos(data || []);
    } catch (e) { console.error(e); }
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { All: photos.length, Before: 0, During: 0, After: 0, Defect: 0, Example: 0, Other: 0 };
    for (const p of photos) c[p.category || 'Other'] = (c[p.category || 'Other'] || 0) + 1;
    return c;
  }, [photos]);

  const visible = useMemo(() => {
    if (filterCategory === 'All') return photos;
    return photos.filter(p => (p.category || 'Other') === filterCategory);
  }, [photos, filterCategory]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('appraisal_id', appraisalId);
      formData.append('description', '');
      formData.append('category', uploadCategory);
      try {
        await api.post('/photos/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) { console.error('Upload failed:', err); }
    }
    setUploading(false);
    load();
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    await api.delete(`/photos/${photoId}`);
    load();
  };

  const updateDesc = async (photoId, desc) => {
    await api.patch(`/photos/${photoId}`, { description: desc });
    load();
  };

  const updateCategory = async (photoId, cat) => {
    await api.patch(`/photos/${photoId}`, { category: cat });
    load();
  };

  // Iter164 — toggle the per-photo client_facing flag.
  // Inspection (Defect/Example/Other) shots default to false; Before/
  // During/After default to true. Operators flip from the lightbox
  // when curating which shots end up in customer PDFs / portal.
  const toggleClientFacing = async (photoId, cur) => {
    await api.patch(`/photos/${photoId}`, { is_client_facing: !cur });
    load();
  };

  return (
    <div data-testid="photo-gallery" className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] flex items-center gap-2">
            <Camera size={18} className="text-[#D35400]" /> Photos
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">{photos.length} photo{photos.length !== 1 ? 's' : ''} · roof inspection &amp; job progress imagery</p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="upload-category" className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">Upload as:</label>
          <select
            id="upload-category"
            data-testid="upload-category-select"
            value={uploadCategory}
            onChange={e => setUploadCategory(e.target.value)}
            className="px-2 py-1 border border-slate-200 rounded text-xs font-semibold bg-white"
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label data-testid="upload-photo-button" className={`flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold cursor-pointer transition-all ${uploading ? 'opacity-50' : ''}`}>
            <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Photos'}
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
      </div>

      {/* Category filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={12} className="text-slate-400" />
        {['All', ...CATEGORIES].map(c => {
          const active = filterCategory === c;
          return (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setFilterCategory(c)}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${active
                ? 'bg-[#D35400] text-white border-[#D35400]'
                : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400]/40'}`}
            >
              {c} <span className="opacity-70 ml-0.5">({counts[c] || 0})</span>
            </button>
          );
        })}
      </div>

      {visible.length === 0 ? (
        <div className="py-10 text-center">
          <Camera size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-400">No photos {filterCategory !== 'All' ? `in the ${filterCategory} category yet` : 'yet. Upload roof inspection photos.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {visible.map(photo => {
            const cat = photo.category || 'Other';
            return (
              <div key={photo._id} data-testid={`photo-${photo._id}`} className="group relative rounded-md overflow-hidden border border-[#E2E2DF] bg-slate-50 hover:shadow-md transition-all">
                <div className="aspect-square cursor-pointer" onClick={() => { setLightbox(photo); setDescription(photo.description || ''); }}>
                  <img src={`${BACKEND_URL}${photo.url}`} alt={photo.description || photo.original_name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <span
                  data-testid={`photo-category-badge-${photo._id}`}
                  className={`absolute top-1.5 left-1.5 text-[10px] font-bold uppercase tracking-[0.08em] px-1.5 py-0.5 border rounded ${CATEGORY_COLORS[cat]}`}
                >{cat}</span>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-[10px] truncate">{photo.description || photo.original_name}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={(e) => { e.stopPropagation(); setLightbox(photo); setDescription(photo.description || ''); }} className="p-1 rounded bg-white/20 text-white hover:bg-white/40"><ZoomIn size={12} /></button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(photo._id); }} className="p-1 rounded bg-red-500/60 text-white hover:bg-red-500"><Trash2 size={12} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-4xl w-full max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="absolute -top-10 right-0 text-white hover:text-slate-300"><X size={24} /></button>
            <img src={`${BACKEND_URL}${lightbox.url}`} alt={lightbox.description} className="w-full max-h-[70vh] object-contain rounded-lg" />
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <MessageSquare size={14} className="text-slate-400 flex-shrink-0" />
              <input data-testid="photo-description-input" value={description} onChange={e => setDescription(e.target.value)} onBlur={() => { if (description !== lightbox.description) updateDesc(lightbox._id, description); }} placeholder="Add description..." className="flex-1 bg-white/10 text-white text-sm px-3 py-2 rounded-md border border-white/20 focus:outline-none focus:border-white/50" />
              <select
                data-testid="lightbox-category-select"
                value={lightbox.category || 'Other'}
                onChange={e => {
                  const next = e.target.value;
                  setLightbox(l => ({ ...l, category: next }));
                  updateCategory(lightbox._id, next);
                }}
                className="px-2 py-1.5 rounded-md bg-white/10 text-white border border-white/20 text-sm focus:outline-none focus:border-white/50"
              >
                {CATEGORIES.map(c => <option key={c} value={c} className="text-slate-900">{c}</option>)}
              </select>
              {/* Iter164 — client-facing toggle. Only checked photos
                  will be rendered in the Remedial Repairs / customer
                  PDFs and the customer portal once it's wired up. */}
              <button
                type="button"
                data-testid="lightbox-client-facing-toggle"
                onClick={() => {
                  setLightbox(l => ({ ...l, is_client_facing: !l.is_client_facing }));
                  toggleClientFacing(lightbox._id, lightbox.is_client_facing);
                }}
                className={`px-2 py-1.5 rounded-md text-sm flex items-center gap-1 border ${
                  lightbox.is_client_facing
                    ? 'bg-emerald-500/30 text-emerald-100 border-emerald-400/50'
                    : 'bg-white/10 text-slate-300 border-white/20'
                }`}
                title={lightbox.is_client_facing
                  ? 'Visible in customer PDFs · click to mark internal-only'
                  : 'Internal-only · click to include in customer PDFs'}
              >
                {lightbox.is_client_facing ? <Eye size={14} /> : <EyeOff size={14} />}
                <span className="text-[11px] font-semibold">{lightbox.is_client_facing ? 'Customer' : 'Internal'}</span>
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-3">
              <span>{lightbox.original_name} · {(lightbox.size / 1024).toFixed(1)} KB</span>
              {lightbox.leak_area_id && (
                <span className="px-2 py-0.5 bg-rose-500/30 text-rose-100 rounded text-[10px] font-semibold uppercase tracking-wide">
                  📍 linked to leak area
                </span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
